import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
	QUOTA_DETAILS_INVOICE_ID_PARAM_NAME,
	QUOTA_DETAILS_PROJECT_ID_PARAM_NAME,
} from "~/constants/appRoutes.ts";
import userViewGuard from "~/hooks/useViewPermssionsCheck.tsx";
import { useCompany } from "~/modules/client/api/company/companyQueries.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import type {
	ValidQuotaDetailsNavigatedFromOrigin,
} from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";
import {
	validQuotaDetailsNavigatedFromOrigins,
} from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";
import { QuotaDetailsView } from "~/modules/quota/components/QuotaDetailsView/QuotaDetailsView.tsx";
import { QUOTA_ACCESS_PERMISSIONS } from "~/modules/quota/utils/quotaPermissionUtils.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";

const QuotaDetailsPage = () => {
	const guard = userViewGuard(QUOTA_ACCESS_PERMISSIONS);
	if (guard) return guard;
	const { quotaId, companyId } = useParams();
	const [searchParams] = useSearchParams();
	const fromParam = searchParams.get("from") as ValidQuotaDetailsNavigatedFromOrigin;
	const projectIdParam = searchParams.get(QUOTA_DETAILS_PROJECT_ID_PARAM_NAME);
	const invoiceIdParam = searchParams.get(QUOTA_DETAILS_INVOICE_ID_PARAM_NAME);

	const navigatedFromSanitised = useMemo(() => {
		if (fromParam && Object.values(validQuotaDetailsNavigatedFromOrigins).includes(fromParam)) {
			return fromParam;
		}
		return undefined;
	}, [fromParam]);

	// The project origin is only usable with its project. Without it, the view falls back to the default origin.
	const navigatedFromProjectId = navigatedFromSanitised === validQuotaDetailsNavigatedFromOrigins.PROJECT
		? projectIdParam ?? undefined
		: undefined;

	// The invoice origin names its invoice. Without it, the view uses the first invoice of the quota.
	const navigatedFromInvoiceId = navigatedFromSanitised === validQuotaDetailsNavigatedFromOrigins.INVOICE
		? invoiceIdParam ?? undefined
		: undefined;

	if (!quotaId || !companyId) return null;
	const {
		data: quotaData,
		isLoading: quotaIsLoading,
	} = useQuery(quotaQueries.details(companyId, quotaId, true));

	const {
		data: companyData,
		isLoading: companyIsLoading,
	} = useCompany(companyId);

	if (quotaIsLoading || companyIsLoading) return <LoadingPage />;
	if (!quotaData || !companyData) return null;

	return <QuotaDetailsView quotaData={quotaData as QuotaWithRelations}
							 companyData={companyData}
							 navigatedFrom={navigatedFromSanitised}
							 navigatedFromInvoiceId={navigatedFromInvoiceId}
							 navigatedFromProjectId={navigatedFromProjectId}
	/>;
};

export default QuotaDetailsPage;
